import { Link } from 'react-router-dom'
import CharacterExternalIconLink from './CharacterExternalIconLink'
import CharacterClassIcon from './CharacterClassIcon'
import CharacterRoleIcon from './CharacterRoleIcon'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import ApplicationRole from 'app/frontend/types/custom/ApplicationRole'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'
import ActivityTimeline from 'app/frontend/base/pages/TeamDashboard/subpages/TeamApplicants/components/ActivityTimeline'
import RaidProgressGrid from 'app/frontend/base/pages/TeamDashboard/subpages/TeamApplicants/components/RaidProgressGrid'
import RefreshCharacterIcon from 'app/frontend/base/pages/TeamDashboard/subpages/TeamApplicants/components/RefreshCharacterIcon'
import { usePageInfo } from '../config/queries'
import { AlertCircleIcon, Check, Loader2 } from 'lucide-react'
import Select from './Select'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import TroubleshootingModal from '../pages/UserDashboard/components/TroubleshootingModal'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import Card from './Card'

dayjs.extend(RelativeTime)

interface Props {
  loading?: boolean
  character?: wowaudit.CharacterReference
  characterDetails?: wowaudit.CharacterDetails
  className?: string
  onEditChange?: (characterId: string) => void
  role?: ApplicationRole
  legacyCharacterPath?: string
  legacyCharacterName?: string
  legacyCharacterClass?: CharacterClass
  legacyCharacterRealm?: wowaudit.Realm
  onRefresh?: () => void
  style?: React.CSSProperties
  team?: wowaudit.Team
}

export const characterOption = (
  char: wowaudit.CharacterReference,
  isSelected: boolean,
) => (
  <div className='flex items-center gap-2.5 w-full whitespace-nowrap'>
    <div className='grow flex items-center gap-1.5'>
      <CharacterClassIcon size='small' characterClass={char.characterClass} />
      {char.name}
    </div>
    <div className='flex items-center gap-2 justify-end'>
      <div className={isSelected ? 'opacity-100' : 'opacity-0'}>
        <Check size={16} />
      </div>

      <span className='text-xs text-muted-foreground'>
        {char.realm.nameWithRegion}
      </span>
    </div>
  </div>
)

const ApplicationCharacterCard = ({
  loading,
  character,
  characterDetails,
  className,
  onEditChange,
  role,
  legacyCharacterPath,
  legacyCharacterName,
  legacyCharacterClass,
  legacyCharacterRealm,
  onRefresh,
  style,
  team,
}: Props) => {
  const {
    data: { user },
  } = usePageInfo()
  const [helpModalOpen, setHelpModalOpen] = useState(false)

  const characterOptions = (user?.characterReferences || []).map((char) => ({
    label: `${char.name} - ${char.realm.nameWithRegion}`,
    customOptionFn: (isSelected: boolean) => characterOption(char, isSelected),
    value: char.id.toString(),
  }))

  if (!character && !legacyCharacterPath)
    return (
      <Alert variant='warning'>
        <AlertCircleIcon className='h-4 w-4' />
        <AlertTitle>No character info</AlertTitle>
        <AlertDescription>
          We could not find any characters linked to this player&apos;s account
          (perhaps due to their privacy settings).
        </AlertDescription>
      </Alert>
    )

  return (
    <Card className={className} style={style}>
      <div className='flex items-center flex-wrap'>
        <div className='flex items-center gap-3'>
          {role ? (
            <CharacterRoleIcon
              role={role}
              size='custom'
              className='h-11 w-11 min-h-11 min-w-11'
            />
          ) : null}
          <div className='flex gap-4 items-center'>
            <Link
              to={character?.path ?? legacyCharacterPath ?? '#'}
              className='font-semibold flex items-center gap-2 text-xl transition-colors'
            >
              <CharacterClassIcon
                characterClass={
                  character?.characterClass ?? legacyCharacterClass
                }
                className='h-11 w-11 min-h-11 min-w-11'
                style='round'
              />
              <div className='flex flex-col'>
                <span className='text-xs text-muted-foreground font-normal'>
                  {character?.realm.nameWithRegion ??
                    legacyCharacterRealm?.nameWithRegion}
                </span>
                <span>{character?.name ?? legacyCharacterName}</span>
              </div>
            </Link>

            {loading ? (
              <Loader2 size={20} className='animate-spin' />
            ) : onEditChange ? (
              <Select
                options={characterOptions}
                value={character?.id.toString()}
                setValue={(value) => {
                  onEditChange(value as string)
                }}
                footerMessage={
                  <div className='flex items-center justify-between gap-2'>
                    <span className='text-xs text-muted-foreground'>
                      {characterOptions.length} character
                      {characterOptions.length === 1 ? '' : 's'}
                    </span>
                    <Button
                      size='xs'
                      variant='outline'
                      onClick={() => setHelpModalOpen(true)}
                    >
                      Help
                    </Button>
                  </div>
                }
                iconOnly
              />
            ) : null}
          </div>
        </div>

        {helpModalOpen && (
          <TroubleshootingModal
            inProfile={false}
            setOpen={(value: boolean) => setHelpModalOpen(value)}
          />
        )}

        {characterDetails ? (
          <>
            <div className='border-l ml-6 pl-6'>
              <div className='text-sm text-muted-foreground mb-1'>
                Item level
              </div>
              <div className={'font-semibold'}>
                {characterDetails.maxItemLevel ?? '-'}
              </div>
            </div>

            <div className='border-l ml-6 pl-6'>
              <div className='text-sm text-muted-foreground mb-1'>M+ score</div>
              <div
                className={'font-semibold'}
                style={{
                  color:
                    characterDetails.mythicRatingColor?.overall || 'inherit',
                }}
              >
                {characterDetails.mythicRating ?? '-'}
              </div>
            </div>
          </>
        ) : null}

        <div className='flex flex-col grow gap-1.5 items-end'>
          <div className='flex gap-2 items-center'>
            <CharacterExternalIconLink
              type='blizzard'
              backendUrl={character?.blizzardUrl}
              realmName={legacyCharacterRealm?.slug}
              region={legacyCharacterRealm?.region}
              entityName={legacyCharacterName}
            />
            <CharacterExternalIconLink
              type='wcl'
              className='w-5 min-w-5'
              backendUrl={character?.wclUrl}
              realmName={legacyCharacterRealm?.slug}
              region={legacyCharacterRealm?.region}
              entityName={legacyCharacterName}
            />
            <CharacterExternalIconLink
              type='raiderio'
              backendUrl={character?.raiderioUrl}
              realmName={legacyCharacterRealm?.slug}
              region={legacyCharacterRealm?.region}
              entityName={legacyCharacterName}
            />
          </div>

          {character && onRefresh ? (
            <div className='flex items-center gap-2 mr-1'>
              <span className='text-xs'>
                updated {dayjs(character.refreshedAt).fromNow()}
              </span>
              <RefreshCharacterIcon
                character={character}
                callback={onRefresh}
                team={team}
              />
            </div>
          ) : null}
        </div>
      </div>

      {characterDetails?.timeline && (
        <div className='mt-3'>
          <div className='text-muted-foreground text-sm font-normal mb-2'>
            Raid history
          </div>
          <ActivityTimeline timeline={characterDetails.timeline} />
        </div>
      )}

      {characterDetails?.currentRaidProgression && character ? (
        <div className='mt-4 pt-4 border-t'>
          <RaidProgressGrid
            characterReference={character}
            progress={characterDetails.currentRaidProgression}
            reports={characterDetails.recentReports}
            style={style}
            team={team}
          />
        </div>
      ) : null}
    </Card>
  )
}

export default ApplicationCharacterCard
